/**
 * PresenceTab — dedicated employee shift planner inside the Planificador.
 *
 * Grid view: rows are employees (Resources with `employeeNif`), columns are
 * days of the visible range (Semana or Mes). Each cell shows the shifts
 * planned for that employee that day (ScheduledItem), with a small status dot
 * derived from the matching PresenceRecords (gk2control fichajes) for past
 * dates: green=asistencia, amber=retraso/salida anticipada/pendiente, red=
 * ausencia, gray=fichaje sin planificación.
 *
 * Click on an empty cell → modal "Nuevo turno" prefilled with that employee
 * and day. Click on a planned shift → modal to edit/delete (reuses the
 * existing ScheduledForm). The "Sincronizar" button pulls all fichajes of
 * the visible range from gk2control.
 *
 * Only employees (Resources with NIF) appear; non-employee Resources stay in
 * the regular Planificación tab as before.
 */
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon, ArrowPathIcon, CheckCircleIcon, XCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import { plannerApi, type PlannerResource, type PlannerScheduledItem, type PresenceRecord, type PlannerTaxonomies } from '@/services/api';
import { LoadingSpinner } from '@/components/common';
import PresenceForm from './PresenceForm';
import PresenceEmployeeCalendar from './PresenceEmployeeCalendar';
import AbsenceForm from './AbsenceForm';

// Visual palette by absence kind. Keep in one place — also used by the
// employee drill-down calendar.
export const ABSENCE_STYLE: Record<string, { chip: string; dot: string; label: string }> = {
  vacations:  { chip: 'bg-vida-700 text-white',       dot: 'bg-vida-700',   label: 'Vacaciones' },
  permission: { chip: 'bg-vida-300 text-vida-900',    dot: 'bg-vida-300',   label: 'Permiso' },
  personal:   { chip: 'bg-amber-300 text-amber-900',  dot: 'bg-amber-300',  label: 'Asuntos propios' },
  sick:       { chip: 'bg-red-500 text-white',        dot: 'bg-red-500',    label: 'Baja' },
  other:      { chip: 'bg-stone-700 text-white',      dot: 'bg-stone-700',  label: 'Otros' },
};

type RangeMode = 'week' | 'month';

function startOfDay(d: Date): Date { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function addDays(d: Date, n: number): Date { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function startOfWeek(d: Date): Date { const x = startOfDay(d); const day = x.getDay(); const diff = (day + 6) % 7; x.setDate(x.getDate() - diff); return x; }
function startOfMonth(d: Date): Date { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d: Date): Date { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function isoDay(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

const LATE_TOLERANCE_MIN = 5;
type CellStatus = { dot: string; title: string } | null;

function computeCellStatus(day: Date, shifts: PlannerScheduledItem[], records: PresenceRecord[], approvedAbsences: PlannerScheduledItem[] = []): CellStatus {
  const isPastOrToday = startOfDay(day).getTime() <= startOfDay(new Date()).getTime();
  if (!isPastOrToday) return null;
  const hasShifts = shifts.length > 0;
  const ins = records.filter((r) => r.type === 'in').sort((a, b) => +new Date(a.date) - +new Date(b.date));
  const outs = records.filter((r) => r.type === 'out').sort((a, b) => +new Date(a.date) - +new Date(b.date));
  const hasIn = ins.length > 0;
  const hasOut = outs.length > 0;
  // Approved absence: never show red ausencia injustificada — render the
  // absence's own color via the chip; the status dot stays neutral here so it
  // doesn't double up.
  if (approvedAbsences.length > 0 && !hasIn && !hasOut) {
    const a = approvedAbsences[0];
    const style = ABSENCE_STYLE[a.absenceKind || 'other'] || ABSENCE_STYLE.other;
    return { dot: style.dot, title: `${style.label} (justificada)` };
  }
  if (!hasShifts && !hasIn && !hasOut) return null;
  if (hasShifts && !hasIn && !hasOut) return { dot: 'bg-red-500', title: 'Ausencia' };
  if (!hasShifts && (hasIn || hasOut)) return { dot: 'bg-gray-400', title: 'Fichaje sin planificación' };

  const plannedStart = new Date(Math.min(...shifts.map((s) => +new Date(s.start))));
  const plannedEnd = new Date(Math.max(...shifts.map((s) => +new Date(s.end))));
  const tags: string[] = [];
  if (hasIn) {
    const firstIn = new Date(ins[0].date);
    const delayMin = Math.floor((+firstIn - +plannedStart) / 60000);
    if (delayMin > LATE_TOLERANCE_MIN) tags.push(`retraso ${delayMin} min`);
  }
  if (hasOut) {
    const lastOut = new Date(outs[outs.length - 1].date);
    const earlyMin = Math.floor((+plannedEnd - +lastOut) / 60000);
    if (earlyMin > LATE_TOLERANCE_MIN) tags.push(`salida anticipada ${earlyMin} min`);
  }
  if (!hasOut && hasIn) {
    return { dot: 'bg-amber-500', title: tags.length ? `Pendiente cerrar salida · ${tags.join(' · ')}` : 'Pendiente cerrar salida' };
  }
  if (tags.length === 0) return { dot: 'bg-green-500', title: 'Asistencia' };
  return { dot: 'bg-amber-500', title: tags.join(' · ') };
}

export default function PresenceTab({ tax: _tax, canManage }: { tax: PlannerTaxonomies | null; canManage: boolean }) {
  const [range, setRange] = useState<RangeMode>('week');
  const [anchor, setAnchor] = useState<Date>(() => startOfDay(new Date()));

  const [resources, setResources] = useState<PlannerResource[]>([]);
  const [items, setItems] = useState<PlannerScheduledItem[]>([]);
  const [absences, setAbsences] = useState<PlannerScheduledItem[]>([]);
  const [presence, setPresence] = useState<PresenceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [modal, setModal] = useState<{ employee: PlannerResource; day: Date; item: PlannerScheduledItem | null; dayPresence: PresenceRecord[] } | null>(null);
  const [absenceModal, setAbsenceModal] = useState<{ employee?: PlannerResource; day?: Date } | null>(null);
  // Drill-down: when an employee row is clicked, show their full week/month
  // calendar instead of the compressed all-employees grid.
  const [selectedEmployee, setSelectedEmployee] = useState<PlannerResource | null>(null);

  // Visible range [from, to)
  const { from, to, days } = useMemo(() => {
    const f = range === 'week' ? startOfWeek(anchor) : startOfMonth(anchor);
    const lastDay = range === 'week' ? addDays(f, 6) : endOfMonth(anchor);
    const t = addDays(lastDay, 1);
    const arr: Date[] = [];
    for (let d = new Date(f); d < t; d = addDays(d, 1)) arr.push(new Date(d));
    return { from: f, to: t, days: arr };
  }, [anchor, range]);

  // Only resources are needed here (no activities/services) — the focused
  // PresenceForm doesn't reference them.
  const loadStatic = async () => {
    try {
      const r = await plannerApi.getResources();
      setResources(r.data?.data || []);
    } catch {
      toast.error('Error al cargar recursos');
    }
  };

  // Range data: scheduled items (shifts + absences) and presence records
  // overlapping the window. Absences are loaded with a separate call so we
  // can paint them with their own colour scheme; work items live in another
  // tab and are not loaded here.
  const loadRange = async () => {
    setLoading(true);
    try {
      const [it, ab, pr] = await Promise.all([
        plannerApi.getScheduled({ from: from.toISOString(), to: to.toISOString(), kind: 'presence' }),
        plannerApi.getScheduled({ from: from.toISOString(), to: to.toISOString(), kind: 'absence' }),
        plannerApi.getPresenceRecords({ dateSince: from.toISOString(), dateUntil: to.toISOString(), limit: 1000 }),
      ]);
      setItems(it.data?.data || []);
      setAbsences(ab.data?.data || []);
      setPresence(pr.data?.data || []);
    } catch {
      toast.error('Error al cargar la planificación');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void loadStatic(); }, []);
  useEffect(() => { void loadRange(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [from, to]);

  // Employees = resources with NIF; non-employee resources are excluded here.
  const employees = useMemo(() => {
    return resources
      .filter((r) => !!r.employeeNif && r.active)
      .sort((a, b) => a.name.localeCompare(b.name, 'es'));
  }, [resources]);

  // Index ScheduledItems by (resourceId|isoDay) and PresenceRecords by NIF|isoDay.
  const itemsByCell = useMemo(() => {
    const m = new Map<string, PlannerScheduledItem[]>();
    for (const it of items) {
      const key = `${it.resourceId}|${isoDay(new Date(it.start))}`;
      (m.get(key) || m.set(key, []).get(key)!).push(it);
    }
    for (const arr of m.values()) arr.sort((a, b) => +new Date(a.start) - +new Date(b.start));
    return m;
  }, [items]);
  const presenceByCell = useMemo(() => {
    const m = new Map<string, PresenceRecord[]>();
    for (const p of presence) {
      const key = `${p.employeeNif}|${isoDay(new Date(p.date))}`;
      (m.get(key) || m.set(key, []).get(key)!).push(p);
    }
    return m;
  }, [presence]);
  // Absences indexed by (resourceId, isoDay). A single absence record covers
  // exactly one day (the backend creates one document per day in a range).
  const absencesByCell = useMemo(() => {
    const m = new Map<string, PlannerScheduledItem[]>();
    for (const a of absences) {
      const key = `${a.resourceId}|${isoDay(new Date(a.start))}`;
      (m.get(key) || m.set(key, []).get(key)!).push(a);
    }
    return m;
  }, [absences]);
  // Pending absences (need approval) — listed in a banner above the grid.
  const pendingAbsences = useMemo(
    () => absences.filter((a) => a.status === 'pending').sort((a, b) => +new Date(a.start) - +new Date(b.start)),
    [absences],
  );
  const employeeName = (resourceId: string) =>
    resources.find((r) => r._id === resourceId)?.name || resourceId.slice(-6);

  const shift = (dir: number) => {
    if (range === 'week') setAnchor((a) => addDays(a, dir * 7));
    else setAnchor((a) => new Date(a.getFullYear(), a.getMonth() + dir, 1));
  };

  const rangeLabel = () => {
    if (range === 'week') return `${from.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })} – ${addDays(to, -1).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}`;
    return anchor.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
  };

  const onCellClick = (employee: PlannerResource, day: Date, existing: PlannerScheduledItem[]) => {
    if (!canManage) return;
    // Fichajes reales del empleado ese dia (read-only en el modal).
    const dayPresence = presenceByCell.get(`${employee.employeeNif}|${isoDay(day)}`) || [];
    setModal({ employee, day, item: existing[0] || null, dayPresence });
  };

  const approveAbsence = async (id: string) => {
    try {
      await plannerApi.approveAbsence(id);
      toast.success('Ausencia aprobada');
      await loadRange();
    } catch (e: unknown) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al aprobar');
    }
  };
  const rejectAbsence = async (id: string) => {
    const reason = window.prompt('Motivo del rechazo (opcional):') || undefined;
    try {
      await plannerApi.rejectAbsence(id, reason ? { reason } : undefined);
      toast.success('Ausencia rechazada');
      await loadRange();
    } catch (e: unknown) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al rechazar');
    }
  };
  const deleteAbsence = async (id: string) => {
    if (!window.confirm('¿Eliminar esta ausencia? Esta acción no se puede deshacer.')) return;
    try {
      await plannerApi.deleteAbsence(id);
      toast.success('Ausencia eliminada');
      await loadRange();
    } catch (e: unknown) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al eliminar');
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      // gk2control's filter expects plain Y-m-d (their docs error: "La fecha
      // inicial debe tener el formato Y-m-d"). We also use local date to avoid
      // the UTC shift at midnight (toISOString would push to the previous day
      // for Spain users near 00:00).
      const r = await plannerApi.syncPresence({ dateSince: isoDay(from), dateUntil: isoDay(addDays(to, -1)) });
      const { inserted = 0, skipped = 0, invalid = 0, reconciled = 0 } = r.data || {};
      toast.success(`Sincronizado: ${inserted} nuevos · ${skipped} repetidos · ${invalid} inválidos · ${reconciled} reasignados`);
      await loadRange();
    } catch (e: unknown) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al sincronizar');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-center gap-3 flex-wrap">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          {(['week', 'month'] as RangeMode[]).map((rm) => (
            <button key={rm} onClick={() => setRange(rm)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md ${range === rm ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
              {rm === 'week' ? 'Semana' : 'Mes'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => shift(-1)} className="p-1.5 rounded-lg hover:bg-gray-100"><ChevronLeftIcon className="h-5 w-5" /></button>
          <span className="px-2 text-sm font-medium text-gray-700 capitalize min-w-[12rem] text-center">{rangeLabel()}</span>
          <button onClick={() => shift(1)} className="p-1.5 rounded-lg hover:bg-gray-100"><ChevronRightIcon className="h-5 w-5" /></button>
          <button onClick={() => setAnchor(startOfDay(new Date()))} className="ml-1 px-2 py-1 text-xs rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">Hoy</button>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {canManage && (
            <button onClick={() => setAbsenceModal({})}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700">
              <PlusIcon className="h-4 w-4" /> Nueva ausencia
            </button>
          )}
          <button onClick={sync} disabled={syncing}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50">
            {syncing ? <LoadingSpinner size="sm" /> : <ArrowPathIcon className="h-4 w-4" />}
            Sincronizar
          </button>
          <div className="hidden md:flex items-center gap-3 text-xs text-gray-500">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-500" /> OK</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Aviso</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Ausencia</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-gray-400" /> Sin plan</span>
          </div>
        </div>
      </div>

      {/* Pendientes de aprobación */}
      {canManage && pendingAbsences.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-amber-900">
              Ausencias pendientes de aprobación ({pendingAbsences.length})
            </h3>
          </div>
          <ul className="space-y-1">
            {pendingAbsences.map((a) => {
              const style = ABSENCE_STYLE[a.absenceKind || 'other'] || ABSENCE_STYLE.other;
              const day = new Date(a.start).toLocaleDateString('es-ES', { weekday: 'short', day: '2-digit', month: 'short' });
              const start = fmtTime(a.start), end = fmtTime(a.end);
              return (
                <li key={a._id} className="flex items-center gap-2 text-sm">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${style.chip}`}>{style.label}</span>
                  <span className="text-gray-700 font-medium">{employeeName(a.resourceId)}</span>
                  <span className="text-gray-500">{day}</span>
                  <span className="text-gray-400 font-mono text-xs">{start}–{end}</span>
                  {a.notes && <span className="text-gray-500 italic truncate max-w-[20rem]">— {a.notes}</span>}
                  <div className="ml-auto flex items-center gap-1">
                    <button onClick={() => approveAbsence(a._id)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-green-700 hover:bg-green-100 rounded"
                      title="Aprobar">
                      <CheckCircleIcon className="h-4 w-4" /> Aprobar
                    </button>
                    <button onClick={() => rejectAbsence(a._id)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-700 hover:bg-red-100 rounded"
                      title="Rechazar">
                      <XCircleIcon className="h-4 w-4" /> Rechazar
                    </button>
                    <button onClick={() => deleteAbsence(a._id)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 rounded"
                      title="Eliminar">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Drill-down: focused calendar for a single employee */}
      {selectedEmployee && (
        <div className="space-y-3">
          <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 px-4 py-3">
            <div>
              <button onClick={() => setSelectedEmployee(null)}
                className="text-xs text-gray-500 hover:text-vida-700 mb-0.5">
                ← Volver a todos los empleados
              </button>
              <div className="text-lg font-semibold text-gray-900">{selectedEmployee.name}</div>
              <div className="text-xs text-gray-400">NIF {selectedEmployee.employeeNif}</div>
            </div>
          </div>
          {loading ? (
            <div className="flex items-center justify-center h-64 text-gray-400"><LoadingSpinner size="lg" /></div>
          ) : (
            <PresenceEmployeeCalendar
              employee={selectedEmployee}
              scope={range}
              anchor={anchor}
              items={items}
              absences={absences}
              presence={presence}
              canManage={canManage}
              onCellClick={(day, existing) => {
                if (!canManage) return;
                const dayPresence = presenceByCell.get(`${selectedEmployee.employeeNif}|${isoDay(day)}`) || [];
                setModal({ employee: selectedEmployee, day, item: existing[0] || null, dayPresence });
              }}
              onNewAbsence={(day) => setAbsenceModal({ employee: selectedEmployee, day })}
              onApproveAbsence={approveAbsence}
              onRejectAbsence={rejectAbsence}
              onDeleteAbsence={deleteAbsence}
            />
          )}
        </div>
      )}

      {/* Overview grid (all employees × days) */}
      {!selectedEmployee && (loading ? (
        <div className="flex items-center justify-center h-64 text-gray-400"><LoadingSpinner size="lg" /></div>
      ) : employees.length === 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-sm text-amber-800">
          No hay recursos con NIF asignado. Para planificar la presencia de un empleado, edita el recurso en la pestaña <strong>Recursos</strong> y rellena su <strong>NIF de empleado</strong>.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-xs uppercase text-gray-500 sticky left-0 bg-gray-50 border-b border-r border-gray-200 min-w-[10rem]">
                  Empleado
                </th>
                {days.map((d) => {
                  const isToday = isoDay(d) === isoDay(new Date());
                  return (
                    <th key={isoDay(d)}
                      className={`text-center px-2 py-2 font-medium text-xs uppercase border-b border-gray-200 min-w-[7rem] ${isToday ? 'text-vida-700 bg-vida-50' : 'text-gray-500'}`}>
                      <div>{d.toLocaleDateString('es-ES', { weekday: 'short' })}</div>
                      <div className="text-gray-700 text-sm">{d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {employees.map((emp) => (
                <tr key={emp._id} className="border-b border-gray-100">
                  <td className="px-3 py-2 sticky left-0 bg-white border-r border-gray-200">
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelectedEmployee(emp); }}
                      className="text-left hover:text-vida-700 transition-colors"
                      title="Ver calendario completo">
                      <div className="font-medium text-gray-900 hover:underline">{emp.name}</div>
                      <div className="text-xs text-gray-400">NIF {emp.employeeNif}</div>
                    </button>
                  </td>
                  {days.map((d) => {
                    const cellKey = `${emp._id}|${isoDay(d)}`;
                    const cellShifts = itemsByCell.get(cellKey) || [];
                    const cellPresence = presenceByCell.get(`${emp.employeeNif}|${isoDay(d)}`) || [];
                    const cellAbsences = absencesByCell.get(cellKey) || [];
                    const approvedAbsences = cellAbsences.filter((a) => a.status === 'approved');
                    const status = computeCellStatus(d, cellShifts, cellPresence, approvedAbsences);
                    const hasShift = cellShifts.length > 0;
                    const hasAbsence = cellAbsences.length > 0;
                    return (
                      <td key={cellKey}
                        onClick={() => onCellClick(emp, d, cellShifts)}
                        title={status?.title}
                        className={`align-top p-1.5 border-r border-gray-100 cursor-pointer hover:bg-gray-50 ${hasShift ? 'bg-vida-50/40' : ''}`}>
                        {(hasShift || hasAbsence) ? (
                          <div className="space-y-1">
                            {cellAbsences.map((a) => {
                              const style = ABSENCE_STYLE[a.absenceKind || 'other'] || ABSENCE_STYLE.other;
                              const isPending = a.status === 'pending';
                              return (
                                <div key={a._id}
                                  title={`${style.label}${isPending ? ' · pendiente' : ''}${a.notes ? ' · ' + a.notes : ''}`}
                                  className={`text-xs px-1.5 py-0.5 rounded whitespace-nowrap overflow-hidden text-ellipsis ${style.chip} ${isPending ? 'opacity-60 ring-1 ring-amber-500' : ''}`}>
                                  {style.label}
                                </div>
                              );
                            })}
                            {cellShifts.map((s) => (
                              <div key={s._id}
                                className="text-xs px-1.5 py-0.5 rounded bg-vida-600 text-white whitespace-nowrap overflow-hidden text-ellipsis">
                                {fmtTime(s.start)}–{fmtTime(s.end)}
                              </div>
                            ))}
                            {status && <span className={`inline-block mt-1 w-2 h-2 rounded-full ${status.dot}`} />}
                          </div>
                        ) : (
                          <div className="h-7 flex items-center justify-center text-gray-300 hover:text-vida-600">
                            {canManage && <PlusIcon className="h-3.5 w-3.5" />}
                            {status && <span className={`ml-2 w-2 h-2 rounded-full ${status.dot}`} />}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {modal && (
        <PresenceForm
          resource={modal.employee}
          initialDate={modal.day}
          initial={modal.item}
          dayPresence={modal.dayPresence}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); void loadRange(); }}
        />
      )}

      {absenceModal && (
        <AbsenceForm
          employees={employees}
          defaultResourceId={absenceModal.employee?._id}
          defaultDate={absenceModal.day}
          onClose={() => setAbsenceModal(null)}
          onSaved={() => { setAbsenceModal(null); void loadRange(); }}
        />
      )}
    </div>
  );
}
